import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { CheckpointResult, ScenarioDetail } from "@/lib/scenarios";

type ResultFailedProps = {
  scenario: ScenarioDetail;
  result: CheckpointResult;
  onResume: () => void;
  onBreak: () => void;
};

/**
 * Failed result (<60%). Tone is encouraging — "почти" not "fail".
 * Lists up to 3 weak items so the user knows what tripped them up,
 * and offers two CTAs: jump back into the session at the weak spots,
 * or take a break and return later (status reverts to IN_PROGRESS).
 *
 * Mockup: `design/hifi/scenarios-extra.jsx` `CheckpointFailScreen`
 * lines ~323–410.
 */
export function ResultFailed({ scenario, result, onResume, onBreak }: ResultFailedProps) {
  const t = useT();
  const wrongItems = result.perItem.filter((p) => !p.correct).slice(0, 3);

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-4 pb-4">
      <div className="flex flex-col items-center gap-3 pt-5 text-center">
        <div
          className="flex size-[72px] items-center justify-center rounded-full"
          style={{ background: "var(--scen-bg)", color: "var(--again)" }}
        >
          <X className="size-9" strokeWidth={2.5} />
        </div>
        <div>
          <h1 className="text-[24px] font-bold leading-[1.2] tracking-[-0.02em] text-text">
            {t("skills.result_failed_title", {
              correct: String(result.correctCount),
              total: String(result.totalItems),
            })}
          </h1>
          <p className="mt-1.5 text-[14px] leading-[1.5] text-text-2">{t("skills.result_failed_subtitle")}</p>
        </div>
      </div>

      {wrongItems.length > 0 && (
        <div className="flex flex-col gap-1.5 px-1">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-wider text-text-3">
            {t("skills.result_failed_weak")}
          </div>
          {wrongItems.map((p) => (
            <div
              key={p.itemSlug}
              className="rounded-[var(--r-md)] border-[1.5px] px-3.5 py-3"
              style={{ borderColor: "var(--again)", background: "var(--again-soft)" }}
            >
              <div className="text-[13px] text-text-2">{p.itemSlug}</div>
              <div className="mt-1 font-mono text-[12px] text-[color:var(--again)]">
                {t("skills.result_review_expected", { answer: p.expected })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show the scenario title quietly so the user knows where they
          are (when the failed screen is the only thing visible). */}
      <div className="px-1 text-center font-mono text-[11px] text-text-3">{scenario.title}</div>

      <div className="flex-1" />

      <div className="flex flex-col gap-2 px-1">
        <Button type="button" variant="scenario" size="block" onClick={onResume}>
          {t("skills.result_failed_cta_resume")}
        </Button>
        <button
          type="button"
          onClick={onBreak}
          className="w-full rounded-[var(--r-md)] border border-border bg-[color:var(--surface-2)] py-3 text-[14px] font-medium text-text-2 transition-colors hover:bg-[color:var(--surface-3)]"
        >
          {t("skills.result_failed_cta_break")}
        </button>
      </div>
    </div>
  );
}
